// src/FeedbackInbox.jsx
import { useEffect, useMemo, useState } from "react";
import { auth, db } from "./firebase.js"; // ✅ single import
import {
  collection,
  getDocs,
  orderBy,
  query,
  updateDoc,
  doc,
  // where, // (optional) remove if unused to avoid lints
} from "firebase/firestore";

const PRIORITY_ORDER = {
  "Critical error": 3,
  "Impacts use of page": 2,
  "Does not impact use of page": 1,
};

// === Google Sheet webhook (same as in FeedbackModal.jsx)
const SHEET_WEBHOOK_URL = import.meta.env.VITE_SHEET_WEBHOOK_URL || "https://script.google.com/macros/s/AKfycbzYpLHFnwBEOjWmo0_B4VHs01gsWr5gjBRPURF_YTgpkjvs1OJvdazVoeXOAzeNreRzcw/exec";
const SHEET_TOKEN = import.meta.env.VITE_SHEET_TOKEN || "3671a113a0";

// Server endpoint that flips feedback status securely
const FEEDBACK_UPDATE_URL =
  "https://us-central1-trelli-prod-473722.cloudfunctions.net/feedbackUpdateStatus";

async function updateFeedbackStatusViaFn(id, status) {
  // Require auth
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in");
  const idToken = await u.getIdToken(/* force */ true);

  const res = await fetch(FEEDBACK_UPDATE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ id, status }),
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`feedbackUpdateStatus failed: ${msg || res.status}`);
  }
}

export default function FeedbackInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [sortKey, setSortKey] = useState("priorityDesc"); // priorityDesc | newest | oldest | topic
  const [errorMsg, setErrorMsg] = useState("");

  const isAuthed = !!auth.currentUser;

  async function load() {
    setLoading(true);
    setErrorMsg(""); // ✅ clear any previous error
    try {
        const q = query(collection(db, "feedback"), orderBy("priority", "desc"));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows);
    } catch (err) {
        console.error("Feedback load failed:", err);
        setErrorMsg(err?.message || "Failed to load feedback.");
    } finally {
        setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const [openItems, doneItems] = useMemo(() => {
    const open = [];
    const done = [];
    for (const it of items) {
      (String(it.status) === "done" ? done : open).push(it);
    }
    return [open, done];
  }, [items]);

  function localSort(arr) {
    const copy = [...arr];
    copy.sort((a, b) => {
      if (sortKey === "priorityDesc") {
        const pa = a.priority ?? PRIORITY_ORDER[a.severity] ?? 1;
        const pb = b.priority ?? PRIORITY_ORDER[b.severity] ?? 1;
        if (pb !== pa) return pb - pa;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
      if (sortKey === "newest") {
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
      if (sortKey === "oldest") {
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      }
      if (sortKey === "topic") {
        return String(a.topic || "").localeCompare(String(b.topic || ""));
      }
      return 0;
    });
    return copy;
  }

  async function markDoneAndMove(it) {
    try {
      // 1) Secure server-side update (bypasses client rules safely)
      await updateFeedbackStatusViaFn(it.id, "done");

      // 2) Move the row in Google Sheet: Open -> Completed (best-effort)
      try {
        await fetch(SHEET_WEBHOOK_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify({
            token: SHEET_TOKEN,
            action: "complete",
            feedbackId: it.id,
            completedBy: auth.currentUser?.email || "unknown@noemail",
            completedAt: new Date().toISOString(),
          }),
        });
      } catch (e) {
        console.warn("Sheet move failed:", e);
      }

      // 3) Optimistic UI: remove from open list immediately
      setItems((prev) =>
        prev.map((r) => (r.id === it.id ? { ...r, status: "done" } : r))
      );

      // 4) Fresh reload to stay in sync
      await load();
    } catch (err) {
      console.error("Mark done failed:", err);
      setErrorMsg(err?.message || "Could not mark as done.");
    }
  }

  async function markOpen(id) {
    try {
      await updateFeedbackStatusViaFn(id, "open");
      // Optimistic UI
      setItems((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: "open" } : r))
      );
      await load();
    } catch (err) {
      console.error("Reopen failed:", err);
      setErrorMsg(err?.message || "Could not reopen item.");
    }
  }

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  };
  const thtd = {
    borderBottom: "1px solid #e5e5e5",
    padding: "10px 8px",
    textAlign: "left",
    verticalAlign: "top",
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>MVP Feedback Inbox</h2>

      {errorMsg && (
        <div
            style={{
            margin: "8px 0 16px",
            padding: 12,
            borderRadius: 8,
            background: "#ffebee",
            border: "1px solid #ef9a9a",
            color: "#b71c1c",
            }}
        >
            {errorMsg}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <label style={{ fontSize: 14 }}>
          Sort:&nbsp;
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={{ padding: 6, borderRadius: 8, border: "1px solid #ccc" }}
          >
            <option value="priorityDesc">Priority (Critical first)</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="topic">Topic (A–Z)</option>
          </select>
        </label>

        <label style={{ fontSize: 14 }}>
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Show completed
        </label>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          {/* Open items */}
          <h3 style={{ marginTop: 12 }}>Open</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thtd}>Priority</th>
                <th style={thtd}>Topic</th>
                <th style={thtd}>Severity</th>
                <th style={thtd}>Description</th>
                <th style={thtd}>Screenshot</th>
                <th style={thtd}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {localSort(openItems).map((row) => (
                <tr key={row.id}>
                  <td style={thtd} title={row.severity}>
                    {row.priority ?? PRIORITY_ORDER[row.severity] ?? 1}
                  </td>
                  <td style={thtd}>{row.topic || "—"}</td>
                  <td style={thtd}>{row.severity || "—"}</td>
                  <td style={{ ...thtd, whiteSpace: "pre-wrap" }}>
                    {row.description || "—"}
                  </td>
                  <td style={thtd}>
                    {row.imageUrl
                        ? (row.createdBy?.uid === auth.currentUser?.uid
                            ? <a href={row.imageUrl} target="_blank" rel="noreferrer">View</a>
                            : <span style={{ color: "#999" }}>Restricted</span>)
                        : "—"}
                  </td>
                  <td style={thtd}>
                    <button
                        onClick={() => markDoneAndMove(row)}
                        style={{
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: "none",
                            background: "#00B074",
                            color: "#fff",
                            cursor: "pointer",
                            fontWeight: 700,
                        }}
                        >
                        Mark done
                    </button>
                  </td>
                </tr>
              ))}
              {openItems.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...thtd, color: "#666", fontStyle: "italic" }}>
                    No open feedback.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Completed */}
          {showDone && (
            <>
              <h3 style={{ marginTop: 24 }}>Completed</h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thtd}>Priority</th>
                    <th style={thtd}>Topic</th>
                    <th style={thtd}>Severity</th>
                    <th style={thtd}>Description</th>
                    <th style={thtd}>Screenshot</th>
                    <th style={thtd}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {localSort(doneItems).map((row) => (
                    <tr key={row.id}>
                      <td style={thtd} title={row.severity}>
                        {row.priority ?? PRIORITY_ORDER[row.severity] ?? 1}
                      </td>
                      <td style={thtd}>{row.topic || "—"}</td>
                      <td style={thtd}>{row.severity || "—"}</td>
                      <td style={{ ...thtd, whiteSpace: "pre-wrap" }}>
                        {row.description || "—"}
                      </td>
                      <td style={thtd}>
                      {row.imageUrl
                            ? (row.createdBy?.uid === auth.currentUser?.uid
                                ? <a href={row.imageUrl} target="_blank" rel="noreferrer">View</a>
                                : <span style={{ color: "#999" }}>Restricted</span>)
                            : "—"}
                      </td>
                      <td style={thtd}>
                        <button
                          onClick={() => markOpen(row.id)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          Reopen
                        </button>
                      </td>
                    </tr>
                  ))}
                  {doneItems.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ ...thtd, color: "#666", fontStyle: "italic" }}>
                        No completed feedback.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}
